import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { Stepper } from "react-form-stepper";
import { useNavigate, useParams } from "react-router-dom";
import { tenderService } from "../../services/api";
import TenderFormDetails from "../../components/club/TenderFormDetails";
import TenderFormRequirements from "../../components/club/TenderFormRequirements";
import {
  PLAYER_POSITIONS,
  STAFF_POSITIONS,
  COACH_POSITIONS,
  AGE_CHOICES,
  FOOT_CHOICES,
  TYPE_OF_TENDER_CHOICES,
  STAFF_TENDER_TYPE_CHOICES,
  SALARY_TYPE_CHOICES,
  MARKET_VALUE_CHOICES,
  TENDER_CATEGORY_CHOICES,
  GAMES_PLAYED_CHOICES,
  LANGUAGE_CHOICES,
  EXPERIENCE_CHOICES,
  RECRUITING_FOR_CHOICES,
} from "../../constants/tenderChoices";


const EditTender = () => {
  const navigate = useNavigate();
  const { tenderId } = useParams();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    tenderFor: TENDER_CATEGORY_CHOICES[0].value,
    recruitingFor: "professional",
    tenderType: TYPE_OF_TENDER_CHOICES[0].value,
    staffTenderType: STAFF_TENDER_TYPE_CHOICES[0].value,
    // DECISION-010: EUR, not the list's first entry (USD). Tender.currency is
    // nullable, so this fallback is reachable for legacy rows — it must not
    // turn them into dollar requests on save.
    currency: "EUR",
    maxTransferFee: "",
    salaryType: SALARY_TYPE_CHOICES[0].value,
    // DECISION-010 family: the form has ONE salary trio and a Gross/Net radio
    // on top of it (TenderFormDetails). Without this key the radio matches
    // neither option and renders unchecked, which is how a gross request used
    // to open with empty salary fields. "net" mirrors CreateTender's default.
    salaryCalculationType: "net",
    netSalary: "",
    salaryRange: { min: "", max: "" },
    hasBonus: false,
    position: "",
    // DECISION-008: no pre-selected foot. A stored request without a foot must
    // not silently acquire "Right" the moment it is opened for editing.
    foot: "",
    minHeight: "",
    minAge: "",
    maxAge: "",
    age: AGE_CHOICES[0].value,
    marketValueType: MARKET_VALUE_CHOICES[0].value,
    marketValue: "",
    marketValueRange: { min: "", max: "" },
    description: "",
    otherRequirements: "",
    documents: [],
    matchesPlayedLastSeason: "",
    matchesPlayedLastHalfSeason: "",
    experience: "",
    languageSkills: [],
    onlyEuPlayer: false,
    // DECISION-010: no silent "free agents only" (the loaded request overwrites
    // this from only_free_players, but the pre-load state must not lie either).
    onlyFreePlayers: false,
    loanFee: false,
    loanFeeAmount: "",
    loanWithOptionToBuy: true,
    durationInMonths: "",
    trialPeriod: { start: "", end: "" },
    travel: false,
    housing: false,
    foodCovered: false,
    offeringAdditionalBenefits: true,
    gamesPlayedIn: GAMES_PLAYED_CHOICES[0].value,
    staffPosition: "",
    toleranceValue: null,
    existingDocument: null,
    documentMarkedForRemoval: false,
  });

  const steps = useMemo(
    () => [
      { key: "details", label: "Request Details" },
      { key: "requirements", label: "Requirements" },
    ],
    []
  );

  useEffect(() => {
    const loadTender = async () => {
      try {
        const response = await tenderService.getSingleTender(tenderId);
        if (!response.success || !response.data) {
          toast.error(response.error || "Failed to load request");
          navigate("/club/tenders");
          return;
        }

        const t = response.data;
        const category = t.tender_category || (t.category || "player");
        const isPlayer = category === "player";

        const resolveAbsoluteUrl = (url) => {
          if (!url) return null;
          if (/^https?:\/\//i.test(url)) return url;
          const base = process.env.REACT_APP_API_ENDPOINT || "";
          const normalizedBase = base.endsWith("/") ? base.slice(0, -1) : base;
          const normalizedPath = url.startsWith("/") ? url.slice(1) : url;
          return normalizedBase ? `${normalizedBase}/${normalizedPath}` : url;
        };

        const documentSource = t.document_url || t.document || (Array.isArray(t.documents) && t.documents.length > 0 ? t.documents[0] : null);
        let existingDocument = null;

        if (documentSource) {
          if (typeof documentSource === "string") {
            const absoluteUrl = resolveAbsoluteUrl(documentSource);
            existingDocument = {
              url: absoluteUrl,
              name: t.document_name || documentSource.split("/").pop() || "Document",
              type: t.document_content_type || null,
              size: t.document_size || null,
            };
          } else if (typeof documentSource === "object") {
            const sourceUrl = documentSource.url || documentSource.document_url || documentSource.file || documentSource.path || null;
            const absoluteUrl = resolveAbsoluteUrl(sourceUrl);
            const derivedName =
              documentSource.name ||
              t.document_name ||
              (absoluteUrl ? absoluteUrl.split("/").pop() : null) ||
              "Document";

            existingDocument = {
              url: absoluteUrl,
              name: derivedName,
              type:
                documentSource.content_type ||
                documentSource.type ||
                t.document_content_type ||
                null,
              size: documentSource.size || t.document_size || null,
            };
          }
        }

        // Salary: the API keeps net and gross in SEPARATE columns, while the
        // form has one from/to/fixed trio plus the Gross/Net radio. Infer the
        // mode from which side carries a value and load that side into the
        // trio, the same rule the agent-side edit screen already uses
        // (pages/agent-tender/EditAgentTender.js:33-39).
        //
        // Gross deliberately wins the tie. A request edited before this fix
        // had a net 0 PATCHed onto it while its gross figure stayed in the
        // database, and net-wins would keep showing that 0 forever; gross-wins
        // recovers the real figure the moment the request is opened.
        const hasValue = (v) => v !== null && v !== undefined && v !== "";
        const isGrossSalary =
          hasValue(t.gross_salary_from) ||
          hasValue(t.gross_salary_to) ||
          hasValue(t.fix_gross_salary);
        const salaryFrom = isGrossSalary ? t.gross_salary_from : t.net_salary_from;
        const salaryTo = isGrossSalary ? t.gross_salary_to : t.net_salary_to;
        const salaryFixed = isGrossSalary ? t.fix_gross_salary : t.fix_net_salary;

        const nextData = {
          tenderFor: category,
          recruitingFor: t.recruiting_for || "professional",
          tenderType: isPlayer ? (t.type_of_tender || "buy") : formData.tenderType,
          staffTenderType: !isPlayer ? (t.type_of_tender || "full_time") : formData.staffTenderType,
          currency: t.currency || formData.currency,
          maxTransferFee: t.maximum_transfer_fee ? String(t.maximum_transfer_fee) : "",
          salaryType: t.salary_type || formData.salaryType,
          salaryCalculationType: isGrossSalary ? "gross" : "net",
          netSalary: salaryFixed ? String(salaryFixed) : "",
          salaryRange: {
            min: salaryFrom ? String(salaryFrom) : "",
            max: salaryTo ? String(salaryTo) : "",
          },
          hasBonus: Boolean(t.bonus),
          position: t.position || "",
          foot: t.foot || formData.foot,
          minHeight: t.minimum_height ? String(t.minimum_height) : "",
          minAge: t.min_age ? String(t.min_age) : "",
          maxAge: t.max_age ? String(t.max_age) : "",
          marketValueType: t.market_value_type || formData.marketValueType,
          marketValue: t.fix_market_value ? String(t.fix_market_value) : "",
          marketValueRange: {
            min: t.market_value_from ? String(t.market_value_from) : "",
            max: t.market_value_to ? String(t.market_value_to) : "",
          },
          description: t.description || "",
          otherRequirements: t.key_requirements || "",
          documents: [],
          matchesPlayedLastSeason: t.matches_played_last_season ? String(t.matches_played_last_season) : "",
          matchesPlayedLastHalfSeason: t.matches_played_last_half_season ? String(t.matches_played_last_half_season) : "",
          experience: t.experience || "",
          languageSkills: Array.isArray(t.language_skills) ? t.language_skills : [],
          onlyEuPlayer: Boolean(t.only_eu_player),
          onlyFreePlayers: Boolean(t.only_free_players),
          loanFee: Boolean(t.has_loan_fee),
          loanFeeAmount: t.loan_fee_amount ? String(t.loan_fee_amount) : "",
          loanWithOptionToBuy: Boolean(t.loan_with_option_to_buy),
          durationInMonths: t.temporary_duration ? String(t.temporary_duration) : "",
          trialPeriod: {
            start: t.trial_period_start_date || "",
            end: t.trial_period_end_date || "",
          },
          travel: Boolean(t.travel),
          housing: Boolean(t.housing),
          foodCovered: Boolean(t.food_covered),
          offeringAdditionalBenefits: Boolean(t.offering_additional_benefits),
          // The games-played choice is not stored on the backend; infer it
          // from the saved counts so editing pre-selects the right option
          // (a count > 0 picks its period, otherwise "not important").
          gamesPlayedIn: t.matches_played_last_season
            ? "last_season"
            : t.matches_played_last_half_season
              ? "last_half_season"
              : "not_important",
          staffPosition: t.other_staff_position || "",
          toleranceValue: typeof t.tolerance_value === "number" ? t.tolerance_value : null,
          existingDocument,
          documentMarkedForRemoval: false,
        };

        setFormData(nextData);
      } catch (e) {
        toast.error("Failed to load request");
        navigate("/club/tenders");
      } finally {
        setInitialLoading(false);
      }
    };

    loadTender();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenderId]);

  const handleNext = () => {
    if (loading) return;
    setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const handleBack = () => {
    if (loading) return;
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const buildUpdatePayload = () => {
    const isPlayer = formData.tenderFor === "player";
    const isGrossSalary = formData.salaryCalculationType === "gross";
    const isFixedSalary = formData.salaryType === "fixed_amount";
    const isSalaryRange = formData.salaryType === "salary_between";
    const payload = {
      tender_category: formData.tenderFor,
      recruiting_for: formData.recruitingFor,
      type_of_tender: isPlayer ? formData.tenderType : formData.staffTenderType,
      currency: formData.currency,
      position: formData.position || null,
      salary_type: formData.salaryType,
      // Both column families are always sent, one of them as null, so that
      // switching Gross <-> Net clears the side the user left behind instead
      // of leaving a stale figure that later reads as the request's salary.
      // Same shape as CreateTender's payload (CreateTender.js:328-350).
      fix_net_salary: isFixedSalary && !isGrossSalary ? Number(formData.netSalary || 0) : null,
      fix_gross_salary: isFixedSalary && isGrossSalary ? Number(formData.netSalary || 0) : null,
      net_salary_from: isSalaryRange && !isGrossSalary ? Number(formData.salaryRange.min || 0) : null,
      net_salary_to: isSalaryRange && !isGrossSalary ? Number(formData.salaryRange.max || 0) : null,
      gross_salary_from: isSalaryRange && isGrossSalary ? Number(formData.salaryRange.min || 0) : null,
      gross_salary_to: isSalaryRange && isGrossSalary ? Number(formData.salaryRange.max || 0) : null,
      bonus: Boolean(formData.hasBonus),
      min_age: formData.minAge ? Number(formData.minAge) : null,
      max_age: formData.maxAge ? Number(formData.maxAge) : null,
      description: formData.description,
      key_requirements: formData.otherRequirements,
      matches_played_last_season: formData.matchesPlayedLastSeason
        ? parseInt(formData.matchesPlayedLastSeason)
        : 0,
      matches_played_last_half_season: formData.matchesPlayedLastHalfSeason
        ? parseInt(formData.matchesPlayedLastHalfSeason)
        : 0,
      experience: formData.experience || null,
      language_skills: formData.languageSkills || [],
      only_eu_player: formData.onlyEuPlayer || false,
      only_free_players: formData.onlyFreePlayers || false,
      tolerance_value: formData.toleranceValue ? parseInt(formData.toleranceValue) : null,
    };

    if (isPlayer) {
      payload.market_value_type = formData.marketValueType;
      payload.fix_market_value =
        formData.marketValueType === "market_value_fixed"
          ? Number(formData.marketValue || 0)
          : null;
      payload.market_value_from =
        formData.marketValueType === "market_value_range"
          ? Number(formData.marketValueRange.min || 0)
          : null;
      payload.market_value_to =
        formData.marketValueType === "market_value_range"
          ? Number(formData.marketValueRange.max || 0)
          : null;
      // DECISION-008: only send a foot that is actually set — never "".
      if (formData.foot) {
        payload.foot = formData.foot;
      }
      payload.minimum_height = formData.minHeight ? parseInt(formData.minHeight) : null;

      if (formData.tenderType === "buy") {
        payload.maximum_transfer_fee = formData.maxTransferFee
          ? Number(formData.maxTransferFee)
          : null;
      }
      if (formData.tenderType === "loan") {
        payload.has_loan_fee = formData.loanFee || false;
        payload.loan_fee_amount = formData.loanFee && formData.loanFeeAmount
          ? Number(formData.loanFeeAmount)
          : null;
        payload.loan_with_option_to_buy = formData.loanWithOptionToBuy || false;
      }
      if (formData.tenderType === "trial") {
        payload.trial_period_start_date = formData.trialPeriod.start || null;
        payload.trial_period_end_date = formData.trialPeriod.end || null;
        payload.travel = formData.travel || false;
        payload.housing = formData.housing || false;
        payload.food_covered = formData.foodCovered || false;
      }
    } else {
      if (formData.staffTenderType === "contract") {
        payload.temporary_duration = formData.durationInMonths
          ? parseInt(formData.durationInMonths)
          : 0;
      }
      payload.other_staff_position = formData.staffPosition || null;
      payload.offering_additional_benefits = formData.offeringAdditionalBenefits || false;
    }

    if (formData.documentMarkedForRemoval) {
      payload.document = null;
    }

    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (activeStep < steps.length - 1) return;
    setLoading(true);
    setError(null);
    try {
      const payload = buildUpdatePayload();
      let response;

      if (formData.documents && formData.documents.length > 0) {
        const formDataToSend = new FormData();

        Object.entries(payload).forEach(([key, value]) => {
          if (value === null || value === undefined) {
            return;
          }

          if (Array.isArray(value)) {
            value.forEach((item) => {
              if (item !== null && item !== undefined) {
                formDataToSend.append(key, item);
              }
            });
          } else {
            formDataToSend.append(key, value);
          }
        });

        formDataToSend.append("document", formData.documents[0]);
        response = await tenderService.updateTender(tenderId, formDataToSend);
      } else {
        response = await tenderService.updateTender(tenderId, payload);
      }
      if (response.success) {
        toast.success("Request updated");
        navigate("/club/tenders");
      } else {
        setError(response.error);
        toast.error(response.error || "Failed to update request");
      }
    } catch (err) {
      const message = "Failed to update request. Please try again.";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="sm:px-12 px-4 py-6">
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-500 hover:text-gray-700"
            aria-label="Go back to Club Requests"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12.8337 6.99935H1.16699M1.16699 6.99935L7.00033 12.8327M1.16699 6.99935L7.00033 1.16602"
                stroke="#2C2C2C"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <h1 className="text-xl font-semibold text-gray-900">Edit Request</h1>
        </div>

        <div className="mb-4 grid lg:grid-cols-3 items-center">
          <div className="justify-self-start" />
          <div className="justify-self-center w-full max-w-2xl">
            <Stepper
              steps={steps.map((s) => ({ label: s.label }))}
              activeStep={activeStep}
              connectorStateColors
              styleConfig={{
                activeBgColor: "#2283FF",
                completedBgColor: "#2283FF",
                inactiveBgColor: "#E6E6E6",
                activeTextColor: "#FFFFFF",
                completedTextColor: "#FFFFFF",
                inactiveTextColor: "#1E1E1E",
                size: "28px",
                borderRadius: "50%",
              }}
              connectorStyleConfig={{
                activeColor: "#2283FF",
                completedColor: "#2283FF",
                disabledColor: "#E6E6E6",
                size: 1,
              }}
            />
          </div>
          <div className="justify-self-end flex items-center gap-3">
            {activeStep > 0 && (
              <button
                type="button"
                onClick={handleBack}
                disabled={loading}
                className="px-4 py-2 text-sm text-[#1E1E1E] disabled:opacity-50 border border-[#E6E6E6] shadow-md rounded-lg cursor-pointer"
                aria-label="Go to previous step"
              >
                Back
              </button>
            )}
            {activeStep < steps.length - 1 && (
              <button
                type="button"
                onClick={handleNext}
                disabled={loading}
                className="px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 disabled:opacity-50 cursor-pointer"
                aria-label="Go to next step"
              >
                Next
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 text-red-500 rounded-lg">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {activeStep === 0 && (
            <div className="bg-gray-50 rounded-lg p-0.5">
              <TenderFormDetails
                formData={formData}
                setFormData={setFormData}
                choices={{
                  tenderTypes: TYPE_OF_TENDER_CHOICES,
                  staffTenderTypes: STAFF_TENDER_TYPE_CHOICES,
                  salaryTypes: SALARY_TYPE_CHOICES,
                  recruitingForChoices: RECRUITING_FOR_CHOICES,
                }}
              />
            </div>
          )}

          {activeStep === 1 && (
            <div className="bg-gray-50 rounded-lg p-0.5">
              <TenderFormRequirements
                formData={formData}
                setFormData={setFormData}
                choices={{
                  playerPositions: PLAYER_POSITIONS,
                  // Coach roles + non-coach staff in one Staff Position list so
                  // editing a coach tender keeps head_coach/assistant_coach/…
                  // selectable. See CreateTender for the 9bc7d52 regression
                  // rationale and the Coaches-tab classification dependency.
                  staffPositions: [...COACH_POSITIONS, ...STAFF_POSITIONS],
                  footChoices: FOOT_CHOICES,
                  marketValueTypes: MARKET_VALUE_CHOICES,
                  gamesPlayedChoices: GAMES_PLAYED_CHOICES,
                  languageChoices: LANGUAGE_CHOICES,
                  experienceChoices: EXPERIENCE_CHOICES,
                }}
              />
            </div>
          )}
          {activeStep === 1 && (
            <div className="flex gap-4 mt-6">
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 disabled:opacity-50 cursor-pointer"
                aria-label="Update request"
              >
                {loading ? "Updating..." : "Update Request"}
              </button>
              <button
                type="button"
                onClick={() => navigate("/club/tenders")}
                className="px-4 py-2 text-[#1E1E1E] border border-[#E6E6E6] shadow-md rounded-lg cursor-pointer"
                aria-label="Cancel and go back"
              >
                Cancel
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default EditTender;

